import { ReactElement } from 'react';
import { useRoutes, useAwsEffect, useAwsGetter } from '@localstack/services';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardActions } from '@mui/material';
import { LambdaCreateCapacityProviderRequest, LambdaUpdateCapacityProviderRequest } from '@localstack/types';
import { ProgressButton, LambdaCapacityProviderForm, PageTitle } from '@localstack/ui';
import { DEFAULT_LAMBDA_ROUTES } from '@localstack/constants';

import { LambdaProps } from './types';

export const LambdaCapacityProviderUpsert = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_LAMBDA_ROUTES,
}: LambdaProps): ReactElement => {
  const { goto } = useRoutes();
  const { name } = useParams<'name'>();

  const { createCapacityProvider, updateCapacityProvider, isLoading } = useAwsEffect(
    'Lambda',
    ['createCapacityProvider', 'updateCapacityProvider'],
    {
      revalidate: ['listCapacityProviders', 'getCapacityProvider'],
    },
  );

  const { data: capacityProvider } = useAwsGetter('Lambda', 'getCapacityProvider', [{ CapacityProviderName: name }], {
    clientOverrides,
    enable: !!name,
  });

  const handleCreate = async (data: LambdaCreateCapacityProviderRequest) => {
    if (await createCapacityProvider(data)) {
      goto(routes.RESOURCES_LAMBDA_CAPACITY_PROVIDERS);
    }
  };

  const handleUpdate = async (data: LambdaUpdateCapacityProviderRequest) => {
    if (await updateCapacityProvider(data)) {
      goto(routes.RESOURCES_LAMBDA_CAPACITY_PROVIDER, { name });
    }
  };

  const CREATE_OR_UPDATE_TEXT = name ? 'Update' : 'Create';

  return (
    <Layout
      documentTitle={`Lambda: ${CREATE_OR_UPDATE_TEXT} Capacity Provider`}
      title={
        <PageTitle
          title={`${CREATE_OR_UPDATE_TEXT} Capacity Provider`}
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Capacity Providers', () => goto(routes.RESOURCES_LAMBDA_CAPACITY_PROVIDERS)],
            [CREATE_OR_UPDATE_TEXT, null],
          ]}
        />
      }
      actions={
        <ProgressButton type="submit" form="update" variant="outlined" color="primary" loading={isLoading}>
          Submit
        </ProgressButton>
      }
    >
      <Card>
        <CardContent>
          <LambdaCapacityProviderForm
            data={capacityProvider?.CapacityProvider}
            name={name}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
            loading={isLoading}
            formId="update"
          />
        </CardContent>
        <CardActions>
          <ProgressButton type="submit" form="update" variant="contained" color="primary" loading={isLoading}>
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
